import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import SearchPick, { sortedOptions } from './SearchPick';
import { ruleText, withRule, withChosenRule, withConfirmedRule, isDerived } from './dataGraphApi';

// The dialog behind a listed object's Edit (4.2.8, 2026-11-05): how the object is reached, with room to
// read. Three parts: the object's RULES (each `Object.column IN Source.column`, tagged derived or given,
// with Confirm for a derived one, its "at most N per parent" cap and Remove); the ALTERNATIVES from the
// profile's links (the planner's ranked candidates — one full-width row each, the rule beside its reason
// and its own button, so which button changes which rule is never in doubt; "Use instead" takes a derived
// rule's place, "Use" adds one more); and ADD A RULE, whose column, source and source-column pickers are
// alphabetised search boxes. Every change goes to the page's draft at once through `onUpdate` — the page's
// Save settings is still the one save — so Done only closes. It replaced Change ▾ and a form inlined in
// the cell, where a <select> in profile order and a button per bare row were the painful part.

export const SUGGESTIONS_SHOWN = 8;

// The columns of `name`, loaded once through `columnsOf` (the panel's cached getGraphNode).
const useColumns = (name, columnsOf) => {
  const [cols, setCols] = useState([]);
  useEffect(() => {
    let alive = true;
    setCols([]);
    if (!name || !columnsOf) return undefined;
    Promise.resolve(columnsOf(name)).then((c) => { if (alive) setCols(c || []); }).catch(() => {});
    return () => { alive = false; };
  }, [name, columnsOf]);
  return cols;
};

const candidateRule = (c) => ({ column: c.column, source: c.source, sourceColumn: c.sourceColumn, maxPerParent: null, origin: 'GIVEN' });

/** The ranked candidates the object does not already hold as a rule, the first SUGGESTIONS_SHOWN. */
export const freshCandidates = (object, candidates) => {
  const name = object ? object.name : '';
  const have = new Set(((object && object.rules) || []).map((r) => ruleText(name, r)));
  return (candidates || []).filter((c) => !have.has(ruleText(name, candidateRule(c)))).slice(0, SUGGESTIONS_SHOWN);
};

const KIND_LABEL = { CHILD_LINK: 'its own lookup', PARENT_LINK: 'looked up by the source', SAME_PARENT: 'a shared parent' };

/** "Add a rule": this object's column IN a source's column. The source is the key table or another listed object. */
export const RuleForm = ({ object, sources, columnsOf, onAdd, testId }) => {
  const [column, setColumn] = useState('');
  const [source, setSource] = useState('');
  const [sourceColumn, setSourceColumn] = useState('');
  const ownColumns = useColumns(object, columnsOf);
  const sourceColumns = useColumns(source, columnsOf);
  const sourceNames = useMemo(() => sortedOptions(sources), [sources]);
  const ready = column.trim() && source && sourceColumn.trim();
  const add = () => {
    onAdd({ column: column.trim(), source, sourceColumn: sourceColumn.trim(), maxPerParent: null });
    setColumn(''); setSource(''); setSourceColumn('');
  };
  return (
    <div className='dg-rule-form dg-rule-form--dialog' data-testid={testId}>
      <span className='dg-mono'>{object}.</span>
      <SearchPick value={column} onChange={setColumn} options={ownColumns} placeholder='column' testId={`${testId}-column`} label={`${object} column`} />
      <span>IN</span>
      <SearchPick value={source} onChange={(s) => { setSource(s); setSourceColumn(''); }} options={sourceNames} placeholder='source object'
        testId={`${testId}-source`} label='source object' />
      <span>.</span>
      <SearchPick value={sourceColumn} onChange={setSourceColumn} options={sourceColumns} placeholder='column' testId={`${testId}-source-column`} label='source column' />
      <Button size='sm' disabled={!ready} data-testid={`${testId}-add`} onClick={add}>Add rule</Button>
    </div>
  );
};

/**
 * `object`: the list entry `{ name, save, rules }`; `onUpdate(next)` replaces it in the page's draft.
 * `sources`: the objects a rule may read from. `candidates`: this object's ranked suggestions.
 */
const RuleDialog = ({ show, onHide, object, sources, columnsOf, candidates, onUpdate, testId }) => {
  const rules = (object && object.rules) || [];
  const name = object ? object.name : '';
  const fresh = freshCandidates(object, candidates);
  const derived = rules.some(isDerived);
  const setRules = (next) => onUpdate({ ...object, rules: next });
  const choose = (rule) => onUpdate(withChosenRule([object], name, rule)[0]);
  const setCap = (i, value) => {
    const v = String(value).trim();
    setRules(rules.map((r, j) => (j === i ? { ...r, maxPerParent: v === '' ? null : Number(v) } : r)));
  };
  if (!object) return null;
  return (
    <Modal show={show} onHide={onHide} size='lg' centered dialogClassName='dg-dialog' data-testid={`${testId}-dialog`}>
      <Modal.Header closeButton>
        <Modal.Title as='div' className='dg-dialog__title'>How <span className='dg-mono'>{name}</span> is reached</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='dg-dialog__section' data-testid={`${testId}-dlg-rules`}>
          <div className='dg-dialog__head'>Rules</div>
          {rules.length === 0 && (
            <div className='dg-muted' data-testid={`${testId}-dlg-none`}>No rule yet — the Test derives one, or pick or add one below.</div>
          )}
          {rules.map((r, i) => {
            const id = ruleText(name, r);
            return (
              <div className='dg-dialog__rule' key={id} data-testid={`${testId}-dlg-rule-${i}`}>
                <span className='dg-mono dg-dialog__ruletext' title={id}>{id}</span>
                {isDerived(r) ? (
                  <span className='dg-rule__origin' data-testid={`${testId}-dlg-rule-${i}-derived`}
                    title='The planner’s pick for an object that had no rule: a default. The Test’s counts say whether it is the right one; Confirm keeps it, an alternative below replaces it.'>
                    derived
                  </span>
                ) : (
                  <span className='dg-rule__origin dg-rule__origin--given' title='A rule you chose or confirmed'>given</span>
                )}
                {isDerived(r) && (
                  <Button size='sm' variant='outline-secondary' data-testid={`${testId}-dlg-rule-${i}-confirm`}
                    onClick={() => onUpdate(withConfirmedRule([object], name, i)[0])}>Confirm</Button>
                )}
                <label className='m-0 dg-muted dg-dialog__cap'>at most
                  <input className='dg-input dg-input--cap ml-1' type='number' min='1' value={r.maxPerParent != null ? r.maxPerParent : ''}
                    placeholder='all' onChange={(e) => setCap(i, e.target.value)} data-testid={`${testId}-dlg-rule-${i}-cap`} />
                  {' '}per parent
                </label>
                <Button size='sm' variant='link' className='p-0 dg-dialog__remove' data-testid={`${testId}-dlg-rule-${i}-remove`}
                  onClick={() => setRules(rules.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            );
          })}
        </div>

        {fresh.length > 0 && (
          <div className='dg-dialog__section' data-testid={`${testId}-suggestions`}>
            <div className='dg-dialog__head'>{derived ? 'Alternatives to the derived rule, from the profile’s links' : 'From the profile’s links'}</div>
            <div className='dg-muted mb-2'>Ranked by the planner: nearer the start first, lookups through other columns before owner and audit ones.</div>
            {fresh.map((c, i) => {
              const rule = candidateRule(c);
              return (
                <div className='dg-dialog__suggest' key={ruleText(name, rule)} data-testid={`${testId}-suggestion-${i}`}>
                  <div className='dg-dialog__suggest-text'>
                    <div className='dg-mono dg-dialog__ruletext'>{ruleText(name, rule)}</div>
                    <div className='dg-muted'>
                      {c.reason}
                      {c.kind && KIND_LABEL[c.kind] && <span> · {KIND_LABEL[c.kind]}</span>}
                      {c.polymorphic && <span> · polymorphic</span>}
                    </div>
                  </div>
                  <Button size='sm' variant='outline-primary' data-testid={`${testId}-suggestion-${i}-use`}
                    onClick={() => choose(rule)}>{derived ? 'Use instead' : 'Use'}</Button>
                </div>
              );
            })}
          </div>
        )}

        <div className='dg-dialog__section'>
          <div className='dg-dialog__head'>Add a rule</div>
          {sources.length === 0 ? (
            <div className='dg-muted' data-testid={`${testId}-ruleform-nosource`}>Set the key table first: a rule reads from it or from another listed object.</div>
          ) : (
            <RuleForm object={name} sources={sources} columnsOf={columnsOf} testId={`${testId}-ruleform`}
              onAdd={(rule) => onUpdate(withRule([object], name, { ...rule, origin: 'GIVEN' })[0])} />
          )}
        </div>
        <div className='dg-muted dg-dialog__foot'>Changes are in the page’s draft; Save settings writes them.</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='primary' onClick={onHide} data-testid={`${testId}-done`}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RuleDialog;
